import React, { useState, useEffect } from "react";
import { RefreshCw, AlertCircle, CheckCircle } from "lucide-react";
import { FaHome, FaCheck, FaTimes, FaClock } from "react-icons/fa";
import Modal from './Modal'
import VacateWorkflowTimeline from "./VacateWorkflowTimeline";
import { API_BASE } from '../config'

function decodeIdTokenEmail() {
  try {
    const t = sessionStorage.getItem("id_token");
    if (!t) return null;
    const payload = t.split(".")[1];
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return (JSON.parse(json).email || "").toString().toLowerCase();
  } catch {
    return null;
  }
}

function decodeIdTokenCommunity() {
  try {
    const t = sessionStorage.getItem("id_token");
    if (!t) return null;
    const payload = t.split(".")[1];
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const data = JSON.parse(json);
    return data.community_id || data.communityId;
  } catch {
    return null;
  }
}

function getCommunityId() {
  // First try to get from JWT token
  let communityId = decodeIdTokenCommunity();
  
  // Fallback to sessionStorage if JWT doesn't have it
  if (!communityId) {
    communityId = sessionStorage.getItem("community_id");
  }
  
  return communityId;
}

async function apiFetch(path, opts = {}) {
  const token = sessionStorage.getItem("id_token");
  const resp = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, ...opts.headers },
  });
  if (!resp.ok) throw new Error(`API ${resp.status}`);
  return resp.json();
}

function getStatusBadge(status) {
  const badgeConfig = {
    pending: { bg: "bg-yellow-100", text: "text-yellow-800", icon: FaClock, label: "Pending" },
    admin_review: { bg: "bg-purple-100", text: "text-purple-800", icon: FaClock, label: "In Review" },
    admin_approved: { bg: "bg-green-100", text: "text-green-800", icon: FaCheck, label: "Admin Approved" },
    admin_rejected: { bg: "bg-red-100", text: "text-red-800", icon: FaTimes, label: "Admin Rejected" },
    pending_vacate_confirmation: { bg: "bg-orange-100", text: "text-orange-800", icon: FaClock, label: "Pending Vacate Confirmation" },
    owner_review: { bg: "bg-blue-100", text: "text-blue-800", icon: FaClock, label: "Awaiting Owner" },
    owner_approved: { bg: "bg-teal-100", text: "text-teal-800", icon: FaCheck, label: "Owner Approved" },
    owner_rejected: { bg: "bg-red-100", text: "text-red-800", icon: FaTimes, label: "Owner Rejected" },
    vacate_confirmed: { bg: "bg-green-100", text: "text-green-800", icon: FaCheck, label: "Vacate Confirmed" },
  };

  const config = badgeConfig[status] || badgeConfig.pending;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text} inline-flex items-center gap-1`}>
      <config.icon size={12} />
      {config.label}
    </span>
  );
}

export default function AdminVacateManagement({ communityId: propCommunityId }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [societyDues, setSocietyDues] = useState("clear");
  const email = decodeIdTokenEmail();
  const communityId = propCommunityId || getCommunityId();

  const fetchRequests = async () => {
    if (!communityId || !email) return;
    try {
      setLoading(true);
      const emailParam = email ? `&emailId=${encodeURIComponent(email)}` : "";
      const url = filter ? `/vacate-requests?communityId=${communityId}&status=${filter}${emailParam}` : `/vacate-requests?communityId=${communityId}${emailParam}`;
      const data = await apiFetch(url);
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching vacate requests:", err);
      setError("Failed to load vacate requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [communityId, filter]);

  const handleApprove = async () => {
    if (!selectedRequest) return;
    try {
      setActionLoading(true);
      const payload = {
        COMMUNITY_ID: communityId,
        VACATE_ID: selectedRequest.VACATE_ID,
        ACTION: "admin_approve",
        REVIEWED_BY: email,
        NOTES: reviewNotes,
        SOCIETY_DUES_STATUS: societyDues,
      };

      await apiFetch("/vacate-requests", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setSuccess("Request approved! Awaiting owner confirmation.");
      setSelectedRequest(null);
      setReviewNotes("");
      setSocietyDues("clear");
      fetchRequests();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error approving request:", err);
      setError("Failed to approve request");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !reviewNotes.trim()) {
      setError("Please provide a rejection reason");
      return;
    }
    try {
      setActionLoading(true);
      const payload = {
        COMMUNITY_ID: communityId,
        VACATE_ID: selectedRequest.VACATE_ID,
        ACTION: "admin_reject",
        REVIEWED_BY: email,
        NOTES: reviewNotes,
      };

      await apiFetch("/vacate-requests", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setSuccess("Request rejected.");
      setSelectedRequest(null);
      setReviewNotes("");
      fetchRequests();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error rejecting request:", err);
      setError("Failed to reject request");
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmVacate = async () => {
    if (!selectedRequest) return;
    try {
      setActionLoading(true);
      const payload = {
        COMMUNITY_ID: communityId,
        VACATE_ID: selectedRequest.VACATE_ID,
        ACTION: "admin_confirm_vacate",
        REVIEWED_BY: email,
        NOTES: reviewNotes,
      };

      await apiFetch("/vacate-requests", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setSuccess("Vacate confirmed! Tenant record removed, owner role updated to Resident.");
      setSelectedRequest(null);
      setReviewNotes("");
      fetchRequests();
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      console.error("Error confirming vacate:", err);
      setError("Failed to confirm vacate");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <FaHome className="text-2xl text-blue-600" />
        <h2 className="text-2xl font-bold text-gray-800">Vacate Requests - Admin Review</h2>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg flex items-center gap-2">
          <AlertCircle size={18} />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-lg flex items-center gap-2">
          <CheckCircle size={18} />
          {success}
        </div>
      )}

      {/* Filter */}
      <div className="mb-4 -mx-1 px-1">
        <div className="filter-pills pb-1">
          {["pending", "pending_vacate_confirmation", "admin_approved", "admin_rejected"].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(filter === status ? "" : status)}
              className={`filter-pill px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                filter === status
                  ? status === "pending_vacate_confirmation"
                    ? "bg-orange-500 text-white"
                    : "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-800 active:bg-gray-300"
              }`}
            >
              {status === "pending" ? "Pending" :
               status === "pending_vacate_confirmation" ? "Confirm Vacate ⚠️" :
               status === "admin_approved" ? "Sent to Owner" :
               "Rejected"}
            </button>
          ))}
        </div>
      </div>

      {/* Refresh */}
      <div className="mb-4">
        <button
          onClick={fetchRequests}
          disabled={loading}
          className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Requests List */}
      {loading && <div className="text-center py-8 text-gray-500">Loading requests...</div>}

      {!loading && requests.length === 0 && (
        <div className="text-center py-8 text-gray-500">No vacate requests found</div>
      )}

      {!loading && requests.length > 0 && (
        <div className="space-y-4">
          {requests.map((req) => (
            <div
              key={req.VACATE_ID}
              onClick={() => setSelectedRequest(req)}
              className="border rounded-lg overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition"
            >
              {/* Card header */}
              <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b">
                <div>
                  <p className="font-semibold text-gray-800">Unit {req.UNIT_NBR} — {req.TENANT_NAME || req.TENANT_EMAIL}</p>
                  <p className="text-xs text-gray-500">Submitted {new Date(req.CREATED_AT).toLocaleDateString()}</p>
                </div>
                {getStatusBadge(req.STATUS)}
              </div>
              {/* Workflow timeline (compact) */}
              <div className="px-4 py-3">
                <VacateWorkflowTimeline request={req} />
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedRequest && (
        <Modal title={`Vacate Request — Unit ${selectedRequest.UNIT_NBR}`} onClose={() => setSelectedRequest(null)}>
          <p className="text-sm text-gray-500 mb-4">{selectedRequest.TENANT_NAME || selectedRequest.TENANT_EMAIL}</p>

          {/* Full Workflow Status */}
          <div className="mb-5 p-4 bg-gray-50 rounded-lg border">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Approval Workflow</p>
            <VacateWorkflowTimeline request={selectedRequest} />
          </div>

          {/* Action form — only for pending requests */}
          {selectedRequest.STATUS === "pending" && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Society Dues Status</label>
                <select
                  value={societyDues}
                  onChange={(e) => setSocietyDues(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="clear">Clear</option>
                  <option value="pending">Pending</option>
                  <option value="due">Due</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Review Notes</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Enter any notes for this review..."
                  rows="3"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="flex gap-2">
                <button onClick={() => setSelectedRequest(null)} className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-semibold">Close</button>
                <button onClick={handleReject} disabled={actionLoading} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50">
                  {actionLoading ? "Processing..." : "Reject"}
                  </button>
                  <button onClick={handleApprove} disabled={actionLoading} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50">
                    {actionLoading ? "Processing..." : "Approve → Send to Owner"}
                  </button>
                </div>
              </>
            )}

            {/* Confirm Vacate — owner has approved, admin needs to confirm actual vacate */}
            {selectedRequest.STATUS === "pending_vacate_confirmation" && (
              <>
                <div className="mb-4 p-4 bg-orange-50 border border-orange-300 rounded-lg">
                  <p className="text-sm font-bold text-orange-800 mb-2">⚠️ Action Required: Confirm Vacate</p>
                  <p className="text-sm text-orange-700 mb-2">
                    The owner has approved this vacate request. Before confirming, please verify that:
                  </p>
                  <ul className="text-sm text-orange-700 list-disc list-inside space-y-1">
                    <li>The tenant has physically vacated the premises</li>
                    <li>Keys/access cards have been returned</li>
                    <li>Unit inspection has been completed</li>
                  </ul>
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                    <p className="text-xs font-bold text-red-700">🚨 What will happen when you confirm:</p>
                    <ul className="text-xs text-red-600 list-disc list-inside mt-1 space-y-0.5">
                      <li>Tenant access will be revoked from the system</li>
                      <li>Owner account will be gain Resident access to the Unit until Unit is let out</li>
                      <li>This action cannot be undone</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Confirmation Notes (optional)</label>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="e.g. Tenant vacated on Apr 18, unit inspected and cleared..."
                    rows="3"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>

                <div className="flex gap-2">
                  <button onClick={() => setSelectedRequest(null)} className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-semibold">Cancel</button>
                  <button onClick={handleConfirmVacate} disabled={actionLoading} className="flex-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-bold disabled:opacity-50">
                    {actionLoading ? "Processing..." : "🔒 Confirm Vacate & Delete Tenant"}
                  </button>
                </div>
              </>
            )}

            {!["pending", "pending_vacate_confirmation"].includes(selectedRequest.STATUS) && (
              <button onClick={() => setSelectedRequest(null)} className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold">
                Close
              </button>
            )}
        </Modal>
      )}
    </div>
  );
}
